import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Chip, Alert, IconButton, Tooltip,
  Switch, FormControlLabel, Divider, ToggleButton, ToggleButtonGroup, Autocomplete,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import api from '../services/api';

const EMPTY_ITEM = { type: 'product', product: '', packaging_material: '', quantity: 1 };

export default function ProductKitsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editOpenedRef = useRef(false);
  const [kits, setKits] = useState([]);
  const [products, setProducts] = useState([]);
  const [packagingMaterials, setPackagingMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', is_active: true });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [kitsRes, prodRes, packRes] = await Promise.all([
        api.get('/product-kits/'),
        api.get('/products/'),
        api.get('/packaging-materials/'),
      ]);
      setKits(kitsRes.data?.results ?? kitsRes.data ?? []);
      setProducts(prodRes.data?.results ?? prodRes.data ?? []);
      setPackagingMaterials(packRes.data?.results ?? packRes.data ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (loading || editOpenedRef.current) return;
    const editId = searchParams.get('edit');
    if (!editId) return;
    const kit = kits.find((k) => String(k.id) === String(editId));
    if (!kit) return;
    editOpenedRef.current = true;
    openEdit(kit);
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  }, [loading, kits, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', is_active: true });
    setItems([{ ...EMPTY_ITEM }]);
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (kit) => {
    setEditing(kit);
    setForm({ name: kit.name, description: kit.description || '', is_active: kit.is_active });
    setItems(
      (kit.items || []).map((i) => ({
        type: i.item_type || (i.product ? 'product' : 'packaging'),
        product: i.product || '',
        packaging_material: i.packaging_material || '',
        quantity: i.quantity,
      }))
    );
    setError('');
    setDialogOpen(true);
  };

  const addItem = () => setItems((prev) => [...prev, { ...EMPTY_ITEM }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const setItemField = (idx, field, val) =>
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      if (field === 'type') {
        return { ...it, type: val, product: '', packaging_material: '' };
      }
      return { ...it, [field]: val };
    }));

  const handleSubmit = async () => {
    setError('');
    if (!form.name.trim()) { setError('Введіть назву набору'); return; }
    const validItems = items.filter((i) =>
      i.quantity > 0 && (i.type === 'product' ? i.product : i.packaging_material)
    );
    if (validItems.length === 0) { setError('Додайте хоча б одну позицію'); return; }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        is_active: form.is_active,
        items: validItems.map((i, idx) => ({
          product: i.type === 'product' ? Number(i.product) : null,
          packaging_material: i.type === 'packaging' ? Number(i.packaging_material) : null,
          quantity: Number(i.quantity),
          sort_order: idx,
        })),
      };
      if (editing) {
        await api.put(`/product-kits/${editing.id}/`, payload);
      } else {
        await api.post('/product-kits/', payload);
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Помилка збереження');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити набір?')) return;
    setDeletingId(id);
    try {
      await api.delete(`/product-kits/${id}/`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Помилка видалення');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Комплекти виробів</Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Новий комплект</Button>
        <Tooltip title="Оновити">
          <IconButton onClick={fetchData} disabled={loading}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Назва</TableCell>
                <TableCell>Опис</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Позицій</TableCell>
                <TableCell align="center">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {kits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                    Немає комплектів
                  </TableCell>
                </TableRow>
              ) : (
                kits.flatMap((kit) => [
                  <TableRow key={kit.id} hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpandedId(expandedId === kit.id ? null : kit.id)}>
                        {expandedId === kit.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{kit.name}</TableCell>
                    <TableCell>{kit.description || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={kit.is_active ? 'Активний' : 'Неактивний'}
                        color={kit.is_active ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell>{(kit.items || []).length}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => openEdit(kit)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" disabled={deletingId === kit.id} onClick={() => handleDelete(kit.id)}>
                        {deletingId === kit.id ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                  </TableRow>,
                  expandedId === kit.id && (
                    <TableRow key={`${kit.id}-items`}>
                      <TableCell colSpan={6} sx={{ pl: 6, pb: 2, bgcolor: 'action.hover' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                          Склад комплекту:
                        </Typography>
                        {(kit.items || []).map((item, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                            <Chip size="small" variant="outlined"
                              label={item.item_type === 'packaging' ? 'Упаковка' : 'Виріб'}
                              color={item.item_type === 'packaging' ? 'warning' : 'primary'}
                              sx={{ fontSize: '0.65rem', height: 18 }} />
                            <Typography variant="body2">
                              {item.product_name} — {item.quantity} шт
                            </Typography>
                          </Box>
                        ))}
                      </TableCell>
                    </TableRow>
                  ),
                ].filter(Boolean))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxWidth: 960, width: '96vw' } }}
      >
        <DialogTitle>{editing ? 'Редагувати комплект' : 'Новий комплект'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField fullWidth label="Назва комплекту" value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} sx={{ mb: 2 }} />
          <TextField fullWidth label="Опис (необов'язково)" value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            multiline rows={2} sx={{ mb: 2 }} />
          <FormControlLabel
            control={<Switch checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />}
            label="Активний" sx={{ mb: 2 }} />
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Позиції комплекту</Typography>

          {items.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                gap: 1,
                mb: 1.5,
                alignItems: 'flex-start',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
              }}
            >
              <ToggleButtonGroup
                size="small"
                exclusive
                value={item.type}
                onChange={(_, v) => v && setItemField(idx, 'type', v)}
                sx={{ height: 40, flexShrink: 0 }}
              >
                <ToggleButton value="product" sx={{ fontSize: '0.7rem', px: 1 }}>Виріб</ToggleButton>
                <ToggleButton value="packaging" sx={{ fontSize: '0.7rem', px: 1 }}>Упаковка</ToggleButton>
              </ToggleButtonGroup>

              {item.type === 'product' ? (
                <Autocomplete
                  size="small"
                  sx={{ flex: '1 1 280px', minWidth: 0 }}
                  options={products}
                  getOptionLabel={(o) => (typeof o === 'object' ? o.name || '' : (products.find((p) => p.id === o)?.name || ''))}
                  isOptionEqualToValue={(o, v) => o.id === (typeof v === 'object' ? v.id : v)}
                  value={products.find((p) => p.id === Number(item.product)) || null}
                  onChange={(_, opt) => setItemField(idx, 'product', opt ? opt.id : '')}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Typography variant="body2" sx={{ whiteSpace: 'normal', lineHeight: 1.35 }}>
                        {option.name}
                      </Typography>
                    </li>
                  )}
                  componentsProps={{
                    paper: {
                      sx: {
                        minWidth: '100%',
                        width: 'max-content',
                        maxWidth: 'min(92vw, 880px)',
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Виріб"
                      placeholder="Пошук виробу…"
                      inputProps={{
                        ...params.inputProps,
                        title: products.find((p) => p.id === Number(item.product))?.name || '',
                      }}
                    />
                  )}
                  noOptionsText="Не знайдено"
                />
              ) : (
                <Autocomplete
                  size="small"
                  sx={{ flex: '1 1 280px', minWidth: 0 }}
                  options={packagingMaterials}
                  getOptionLabel={(o) => (typeof o === 'object' ? o.name || '' : (packagingMaterials.find((p) => p.id === o)?.name || ''))}
                  isOptionEqualToValue={(o, v) => o.id === (typeof v === 'object' ? v.id : v)}
                  value={packagingMaterials.find((p) => p.id === Number(item.packaging_material)) || null}
                  onChange={(_, opt) => setItemField(idx, 'packaging_material', opt ? opt.id : '')}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Typography variant="body2" sx={{ whiteSpace: 'normal', lineHeight: 1.35 }}>
                        {option.name}
                      </Typography>
                    </li>
                  )}
                  componentsProps={{
                    paper: {
                      sx: {
                        minWidth: '100%',
                        width: 'max-content',
                        maxWidth: 'min(92vw, 880px)',
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Упаковка"
                      placeholder="Пошук упаковки…"
                      inputProps={{
                        ...params.inputProps,
                        title: packagingMaterials.find((p) => p.id === Number(item.packaging_material))?.name || '',
                      }}
                    />
                  )}
                  noOptionsText="Не знайдено"
                />
              )}

              <TextField type="number" label="К-сть" value={item.quantity}
                onChange={(e) => setItemField(idx, 'quantity', e.target.value)}
                inputProps={{ min: 1 }} sx={{ width: 88, flexShrink: 0 }} size="small" />

              <IconButton size="small" color="error" onClick={() => removeItem(idx)} disabled={items.length === 1}
                sx={{ mt: 0.5, flexShrink: 0 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          <Button size="small" startIcon={<AddIcon />} onClick={addItem} sx={{ mt: 0.5 }}>
            Додати позицію
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
